'use client';

import { useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { Card, Text, Title } from '@tremor/react';
import toast from 'react-hot-toast';
import { Input } from '../../../ui/form/Input';
import { useQueryClient } from 'react-query';
import {
  useWorkspaceSettings,
  workspaceSettingsQueryKey,
} from './useWorkspaceSettings';

const API_HOST = process.env.REACT_APP_API_HOST;

type QuestionSectionKey =
  | 'reviewOpenQuestions'
  | 'goalOpenQuestions'
  | 'engagementSurveyQuestions';

type QuestionsState = Record<QuestionSectionKey, string[]>;

const defaultQuestions: QuestionsState = {
  reviewOpenQuestions: [''],
  goalOpenQuestions: [''],
  engagementSurveyQuestions: [''],
};

const sectionMeta: Array<{
  key: QuestionSectionKey;
  title: string;
  description: string;
  labelPrefix: string;
}> = [
  {
    key: 'reviewOpenQuestions',
    title: 'Review Summary Assistant',
    description:
      'Configure the open-ended prompts used for peer feedback summaries.',
    labelPrefix: 'Review',
  },
  {
    key: 'goalOpenQuestions',
    title: 'Goal Writing Helper',
    description:
      'Define the prompts used to collect context for SMART goal drafts.',
    labelPrefix: 'Goal',
  },
  {
    key: 'engagementSurveyQuestions',
    title: 'Engagement Survey Analyzer',
    description:
      'Set the open-ended questions used in engagement surveys.',
    labelPrefix: 'Survey',
  },
];

const ensureQuestions = (questions?: string[]) => {
  if (!questions || questions.length === 0) {
    return [''];
  }
  return questions;
};

const sanitizeQuestions = (questions: string[]) =>
  questions.map((question) => question.trim()).filter(Boolean);

export function PerformanceEngagementSettings() {
  const [isSaving, setIsSaving] = useState(false);
  const [questions, setQuestions] = useState<QuestionsState>(defaultQuestions);
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useWorkspaceSettings();
  // Saving before the stored questions have loaded would replace them with
  // the empty defaults, so only allow it once they have.
  const canSave = Boolean(data) && !error;

  useEffect(() => {
    const settings = data?.performanceEngagement as QuestionsState | undefined;
    if (!settings) return;

    setQuestions({
      reviewOpenQuestions: ensureQuestions(settings.reviewOpenQuestions),
      goalOpenQuestions: ensureQuestions(settings.goalOpenQuestions),
      engagementSurveyQuestions: ensureQuestions(
        settings.engagementSurveyQuestions,
      ),
    });
  }, [data]);

  useEffect(() => {
    if (!error) return;

    let message = 'Failed to load open questions.';
    if (axios.isAxiosError(error)) {
      message = error.response?.data?.message || error.message || message;
    } else if (error instanceof Error) {
      message = error.message;
    }
    toast.error(message);
  }, [error]);

  const handleQuestionChange = (
    key: QuestionSectionKey,
    index: number,
    value: string,
  ) => {
    setQuestions((prev) => {
      const updated = [...prev[key]];
      updated[index] = value;
      return { ...prev, [key]: updated };
    });
  };

  const handleAddQuestion = (key: QuestionSectionKey) => {
    setQuestions((prev) => ({ ...prev, [key]: [...prev[key], ''] }));
  };

  const handleRemoveQuestion = (key: QuestionSectionKey, index: number) => {
    setQuestions((prev) => {
      const updated = prev[key].filter((_, idx) => idx !== index);
      return { ...prev, [key]: updated.length > 0 ? updated : [''] };
    });
  };

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      const payload = {
        reviewOpenQuestions: sanitizeQuestions(questions.reviewOpenQuestions),
        goalOpenQuestions: sanitizeQuestions(questions.goalOpenQuestions),
        engagementSurveyQuestions: sanitizeQuestions(
          questions.engagementSurveyQuestions,
        ),
      };

      const response = await axios.patch(`${API_HOST}/v1/workspace`, payload);
      if (response.data?.status === 'UPDATE_WORKSPACE_SETTINGS_SUCCESS') {
        await queryClient.invalidateQueries(workspaceSettingsQueryKey);
        toast.success('Open questions updated successfully.');
      } else {
        toast.error(
          response.data?.message || 'Could not update open questions.',
        );
      }
    } catch (error) {
      let message = 'Failed to update open questions.';
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        message = (axiosError.response?.data as any)?.message || axiosError.message;
      } else if (error instanceof Error) {
        message = error.message;
      }
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="mt-6">
        <Title>Performance &amp; Engagement</Title>
        <Text>Loading open questions...</Text>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <Title>Performance &amp; Engagement</Title>
      <Text>Configure open-ended prompts for performance workflows.</Text>

      <div className="mt-6 space-y-8">
        {sectionMeta.map((section) => (
          <div key={section.key} className="space-y-4">
            <div>
              <h4 className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
                {section.title}
              </h4>
              <p className="mt-1 text-tremor-default leading-6 text-tremor-content dark:text-dark-tremor-content">
                {section.description}
              </p>
            </div>

            <div className="space-y-3">
              {questions[section.key].map((question, index) => (
                <div
                  key={`${section.key}-${index}`}
                  className="flex flex-col gap-2 sm:flex-row sm:items-center"
                >
                  <div className="flex-1">
                    <Input
                      aria-label={`${section.labelPrefix} question ${
                        index + 1
                      }`}
                      value={question}
                      onChange={(event) =>
                        handleQuestionChange(
                          section.key,
                          index,
                          event.target.value,
                        )
                      }
                    />
                  </div>
                  <button
                    type="button"
                    className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
                    aria-label={`Remove ${section.labelPrefix.toLowerCase()} question ${
                      index + 1
                    }`}
                    onClick={() =>
                      handleRemoveQuestion(section.key, index)
                    }
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="rounded-md border border-indigo-500 px-3 py-1 text-sm text-indigo-600 hover:bg-indigo-50"
              aria-label={`Add ${section.labelPrefix.toLowerCase()} question`}
              onClick={() => handleAddQuestion(section.key)}
            >
              Add question
            </button>
          </div>
        ))}
      </div>

      {Boolean(error) && (
        <p role="alert" className="mt-8 text-sm text-red-600">
          The saved questions could not be loaded. Reload the page before
          saving, or they will be overwritten.
        </p>
      )}
      <button
        type="button"
        className="text-md !mt-8 w-full rounded-lg bg-[#475467] py-2 font-bold text-white disabled:opacity-60"
        onClick={handleSave}
        disabled={isSaving || !canSave}
      >
        Save open questions
      </button>
    </Card>
  );
}
